import { useState } from 'react';

import { useRouteAndQueryParams } from '@april9au/stack9-react';
import * as ui from '@april9au/stack9-ui';
import { Descriptions, Divider, Form, List, Tag } from 'antd';
import dayjs from 'dayjs';

import ResourceDrawer from '../components/Resource/ResourceDrawer';
import { AppRoutes } from '../constants/appRoutes';
import { DBResourceWithRelated } from '../models/Resource';

const S9PageSection = ui.S9PageSection.default;
const S9CustomEntityFormPage = ui.S9CustomEntityFormPage.default;
const S9Button = ui.S9Button.default;
const S9Skeleton = ui.Skeleton.default;
const S9PageTitle = ui.S9PageTitle.default;
const S9FileField = ui.S9FileField.default;

export const ResourceDetail = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const {
    routeParams: { id = '' },
    queryParams: { callbackUrl = '' },
  } = useRouteAndQueryParams();

  const {
    data: resourceData,
    isValidating: isLoadingResource,
    mutate: mutateResource,
  } = ui.useScreenQueryById<DBResourceWithRelated>('getResourceById', +id);

  // const requirements = resourceData?.requirements
  //   ? parseStringToObject(resourceData.requirements)
  //   : [];
  // const skills_required = resourceData?.skills_required
  //   ? parseStringToObject(resourceData.skills_required)
  //   : [];

  return (
    <S9CustomEntityFormPage
      entityKey="resource"
      screenQueryName="getResourceById"
      entityId={+id}
      title={`Resource #${id}`}
      cancelLink={callbackUrl || AppRoutes.ResourceList}
    >
      {isLoadingResource ? (
        <S9Skeleton active />
      ) : (
        resourceData && (
          <S9PageSection
            size="sm"
            title=" " // Actions only show with a title
            actions={
              <>
                <S9Button
                  label={`${resourceData ? 'Edit' : 'Add'}`}
                  icon={`${resourceData ? 'EditOutlined' : 'PlusOutlined'}`}
                  type="link"
                  onClick={() => setIsDrawerOpen(true)}
                />
              </>
            }
          >
            <S9PageTitle level={4} title="Resource details" />
            <Divider className="my-3" />
            <Descriptions
              className=""
              column={{
                xs: 1,
                sm: 2,
                md: 3,
                lg: 3,
              }}
              items={[
                {
                  label: 'Resource type',
                  children: `${resourceData.resource_type}`,
                },
                {
                  label: 'Title',
                  children: `${resourceData.title}`,
                },
                {
                  label: 'Slug',
                  children: `${resourceData.slug}`,
                },

                {
                  label: 'Files to be downloaded',
                  children: (
                    <Form
                      layout="vertical"
                      initialValues={{
                        resumes: resourceData.file_to_be_downloaded,
                      }}
                    >
                      <S9FileField
                        name="file_to_be_downloaded"
                        entityKey="resource"
                        disabled
                      />
                    </Form>
                  ),
                },
                {
                  label: 'External url',
                  children: resourceData.external_url,
                },
                {
                  label: 'Vimeo video id',
                  children: resourceData.vimeo_video_id,
                },
                {
                  label: 'Video transcript',
                  children: resourceData.video_transcript,
                },
                {
                  label: 'Resource thumbnail',
                  children: (
                    <Form
                      layout="vertical"
                      initialValues={{
                        resumes: resourceData.resource_thumbnail,
                      }}
                    >
                      <S9FileField
                        name="resource_thumbnail"
                        entityKey="resource"
                        disabled
                      />
                    </Form>
                  ),
                },
                {
                  label: 'Is public',
                  children: resourceData.is_public ? 'Yes' : 'No',
                },
                {
                  label: 'Order',
                  children: resourceData.order,
                },
                // {
                //   label: 'Employment type',
                //   children: resourceData.video_transcript,
                // },

                // {
                //   label: 'Published at',
                //   children: resourceData.published_at
                //     ? dayjs(resourceData.published_at).format(
                //         'DD/MM/YYYY HH:mm',
                //       )
                //     : '-',
                // },
                // {
                //   label: 'Expires at',
                //   children: resourceData.expires_at
                //     ? dayjs(resourceData.expires_at).format(
                //         'DD/MM/YYYY HH:mm',
                //       )
                //     : '-',
                // },
                // {
                //   label: 'Requirements',
                //   children: !isEmpty(requirements)
                //     ? requirements.map((value: string) => (
                //         <Tag key={value}>{value}</Tag>
                //       ))
                //     : '-',
                //   span: 3,
                // },
                // {
                //   label: 'Skills required',
                //   children: !isEmpty(skills_required)
                //     ? skills_required.map((value: string) => (
                //         <Tag key={value}>{value}</Tag>
                //       ))
                //     : '-',
                //   span: 3,
                // },
                // {
                //   label: 'Job screening questions',
                //   children: (
                //     <List
                //       size="small"
                //       dataSource={resourceData.job_screening_questions}
                //       renderItem={item => (
                //         <List.Item>
                //           <span>{`${item.title}`}</span>
                //           {item.is_required && (
                //             <span className="text-red-600">
                //               (required)
                //             </span>
                //           )}
                //         </List.Item>
                //       )}
                //     />
                //   ),
                //   span: 3,
                // },
              ]}
            />

            <div className="mt-6" />
            <S9PageTitle level={4} title="Publishing settings" />
            <Divider className="my-3" />
            <Descriptions
              className=""
              column={{
                xs: 1,
                sm: 2,
                md: 3,
                lg: 3,
              }}
              items={[
                {
                  label: 'Published date',
                  children: resourceData.published_date
                    ? dayjs(resourceData.published_date).format(
                        'DD/MM/YYYY HH:mm',
                      )
                    : '-',
                },
                {
                  label: 'Expiry date',
                  children: resourceData.expiry_date
                    ? dayjs(resourceData.expiry_date).format('DD/MM/YYYY HH:mm')
                    : '-',
                },
              ]}
            />

            <div className="mt-6" />
            <S9PageTitle level={4} title="Audience & visibility" />
            <Divider className="my-3" />
            <Descriptions
              className=""
              column={1}
              items={[
                {
                  label: 'Keywords',
                  children: resourceData.keywords
                    ? resourceData.keywords
                        .split(',')
                        .map((value: string) => <Tag key={value}>{value}</Tag>)
                    : '-',
                  span: 3,
                },
                {
                  label: 'Available to organisations',
                  children: (
                    <List
                      size="small"
                      dataSource={resourceData.available_to_organisations}
                      renderItem={item => (
                        <List.Item>
                          <span>{`${item.name}`}</span>
                        </List.Item>
                      )}
                    />
                  ),
                  span: 3,
                },
                {
                  label: 'Topic tags',
                  children: (
                    <List
                      size="small"
                      dataSource={resourceData.topics}
                      renderItem={item => (
                        <List.Item>
                          <span>{`${item.name}`}</span>
                        </List.Item>
                      )}
                    />
                  ),
                  span: 3,
                },
                {
                  label: 'Related articles',
                  children: (
                    <List
                      size="small"
                      dataSource={resourceData.related_articles}
                      renderItem={item => (
                        <List.Item>
                          <span>{`${item.slug} - ${item.title}`}</span>
                        </List.Item>
                      )}
                    />
                  ),
                  span: 3,
                },
              ]}
            />

            <div className="mt-6" />
            <S9PageTitle level={4} title="Featured settings" />
            <Divider className="my-3" />
            <Descriptions
              className=""
              column={{
                xs: 1,
                sm: 2,
                md: 2,
                lg: 2,
                xxl: 2,
              }}
              items={[
                {
                  label: 'Feature on homepage',
                  children: resourceData.feature_on_homepage ? 'Yes' : 'No',
                },
                {
                  label: 'Feature order',
                  children: resourceData.featured_order,
                },
                {
                  label: 'Featured start date',
                  children: resourceData.featured_start_date
                    ? dayjs(resourceData.featured_start_date).format(
                        'DD/MM/YYYY HH:mm',
                      )
                    : '-',
                },
                {
                  label: 'Featured end date',
                  children: resourceData.featured_end_date
                    ? dayjs(resourceData.featured_end_date).format(
                        'DD/MM/YYYY HH:mm',
                      )
                    : '-',
                },
              ]}
            />

            <div className="mt-6" />
            <S9PageTitle level={4} title="Resource type specific settings" />
            <Divider className="my-3" />
            <Descriptions
              className=""
              column={1}
              items={[
                {
                  label: 'Content',
                  children: resourceData.content ? (
                    <div
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{
                        __html: resourceData.content,
                      }}
                    />
                  ) : (
                    '-'
                  ),
                },
              ]}
            />
          </S9PageSection>
        )
      )}
      <ResourceDrawer
        open={isDrawerOpen}
        id={resourceData?.id}
        onSaved={() => {
          setIsDrawerOpen(false);
          mutateResource();
        }}
        onDeleted={() => {
          setIsDrawerOpen(false);
          mutateResource();
        }}
        onClose={() => {
          setIsDrawerOpen(false);
        }}
      />
    </S9CustomEntityFormPage>
  );
};
